import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Form, Button, Alert, Card, Spinner } from 'react-bootstrap';
import authService from '../services/authService';
import businessService from '../services/businessService';
import BackToDashboard from '../components/BackToDashboard';

function RecurringRevenueWorkshop() {
  const navigate = useNavigate();
  const [stage, setStage] = useState('intro'); // intro, conversation, generating, ideas, refine
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Business context
  const [context, setContext] = useState({
    core_product: '',
    industry: '',
    primary_niche: '',
    current_pricing: '',
    product_type: '',
    exit_strategy: ''
  });

  // Conversation state
  const [conversation, setConversation] = useState([]);
  const [userInput, setUserInput] = useState('');
  const [readyForIdeas, setReadyForIdeas] = useState(false);

  // AI-generated ideas
  const [recurringIdeas, setRecurringIdeas] = useState([]);
  const [selectedIdeaIndex, setSelectedIdeaIndex] = useState(null);

  // Refinement data
  const [refinedOneTime, setRefinedOneTime] = useState('');
  const [refinedMonthly, setRefinedMonthly] = useState('');
  const [includedFeatures, setIncludedFeatures] = useState([]);
  const [billingOption, setBillingOption] = useState('monthly');
  const [quarterlyDiscount, setQuarterlyDiscount] = useState('10');
  const [annualDiscount, setAnnualDiscount] = useState('20');
  const [trialPeriod, setTrialPeriod] = useState('7-day');

  useEffect(() => {
    checkAuthAndLoad();
  }, []);

  const checkAuthAndLoad = async () => {
    const authResult = await authService.checkAuth();
    if (!authResult.authenticated) {
      navigate('/login');
      return;
    }

    // Load business data
    const result = await businessService.getFieldsByNames([
      'industry',
      'business_name',
      'pricing_data',
      'exit_strategy',
      'product_type'
    ]);

    // Get core product from Product table
    let coreProductName = '';
    try {
      const productsRes = await fetch('/api/products/get-catalog', { credentials: 'include' });
      if (productsRes.ok) {
        const productsData = await productsRes.json();
        const products = productsData.catalog || [];
        let coreProduct = products.find(p => p.position?.toLowerCase() === 'core');
        if (!coreProduct && products.length > 0) {
          coreProduct = products.find(p => !p.is_primary_tripwire && p.position?.toLowerCase() !== 'tripwire');
        }
        if (coreProduct) {
          coreProductName = coreProduct.name;
        }
      }
    } catch (e) {
      console.error('Error fetching products:', e);
    }

    if (result.success && result.data.fields) {
      const fields = result.data.fields;
      let currentPricing = '';
      let productType = '';

      // Get pricing data
      if (fields.pricing_data) {
        try {
          const pricingData = JSON.parse(fields.pricing_data);
          currentPricing = pricingData.one_time_price || pricingData.price || '';
          productType = pricingData.product_type || '';
        } catch (e) {
          console.error('Error parsing pricing data:', e);
        }
      }

      setContext({
        core_product: coreProductName,
        industry: fields.industry || '',
        primary_niche: '',
        current_pricing: currentPricing,
        product_type: productType || fields.product_type || '',
        exit_strategy: fields.exit_strategy || ''
      });
    }

    // Get primary niche
    try {
      const targetResult = await fetch('/api/target-market/primary', {
        credentials: 'include'
      });
      if (targetResult.ok) {
        const targetData = await targetResult.json();
        if (targetData.primary_niche) {
          setContext(prev => ({
            ...prev,
            primary_niche: targetData.primary_niche.niche_name || ''
          }));
        }
      }
    } catch (e) {
      console.error('Error loading target market:', e);
    }

    // Check for existing conversation
    try {
      const convResult = await fetch('/api/recurring-revenue/get-conversation', {
        credentials: 'include'
      });
      if (convResult.ok) {
        const convData = await convResult.json();
        if (convData.conversation && convData.conversation.length > 0) {
          setConversation(convData.conversation);
          setStage('conversation');
        }
      }
    } catch (e) {
      console.error('Error loading conversation:', e);
    }

    setLoading(false);
  };

  const handleSkip = async () => {
    await businessService.saveField('skipped_recurring_workshop', 'true');
    navigate('/customer-experience');
  };

  const handleStartWorkshop = async () => {
    setGenerating(true);
    setError('');

    try {
      const response = await fetch('/api/recurring-revenue/start-workshop', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(context)
      });

      if (response.ok) {
        const data = await response.json();
        setConversation(data.conversation || []);
        setStage('conversation');
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to start workshop');
      }
    } catch (e) {
      setError('Error starting workshop: ' + e.message);
    }

    setGenerating(false);
  };

  const handleSendMessage = async () => {
    if (!userInput.trim()) return;

    setGenerating(true);
    setError('');

    // Add user message optimistically
    const updatedConversation = [
      ...conversation,
      { role: 'user', content: userInput }
    ];
    setConversation(updatedConversation);
    setUserInput('');

    try {
      const response = await fetch('/api/recurring-revenue/continue-workshop', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          response: userInput,
          conversation: conversation,
          context: context
        })
      });

      if (response.ok) {
        const data = await response.json();
        setConversation(data.conversation || updatedConversation);
        setReadyForIdeas(data.ready_for_ideas || false);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to continue conversation');
      }
    } catch (e) {
      setError('Error: ' + e.message);
    }

    setGenerating(false);
  };

  const handleGenerateIdeas = async () => {
    setGenerating(true);
    setError('');
    setStage('generating');

    try {
      const response = await fetch('/api/recurring-revenue/generate-ideas', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          conversation: conversation,
          context: context
        })
      });

      if (response.ok) {
        const data = await response.json();
        setRecurringIdeas(data.ideas || []);
        setStage('ideas');
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to generate ideas');
        setStage('conversation');
      }
    } catch (e) {
      setError('Error: ' + e.message);
      setStage('conversation');
    }

    setGenerating(false);
  };

  const handleSelectIdea = (index) => {
    setSelectedIdeaIndex(index);
    const idea = recurringIdeas[index];
    setRefinedOneTime(idea.one_time_price || '');
    setRefinedMonthly(idea.monthly_price || '');
    setIncludedFeatures(idea.included_features || []);
    setStage('refine');
  };

  const handleSaveModel = async () => {
    if (!refinedOneTime || !refinedMonthly) {
      setError('Please enter both one-time and monthly pricing');
      return;
    }

    setSaving(true);
    setError('');

    const selectedIdea = recurringIdeas[selectedIdeaIndex];

    const updatedPricingData = {
      pricing_model: 'hybrid',
      billing_frequency: 'hybrid',
      is_recurring: true,
      hybrid_one_time: refinedOneTime,
      hybrid_monthly: refinedMonthly,
      billing_option: billingOption,
      quarterly_discount: quarterlyDiscount,
      annual_discount: annualDiscount,
      trial_period: trialPeriod,
      included_features: includedFeatures
    };

    const recurringModelDetails = {
      model_name: selectedIdea.model_name,
      structure: selectedIdea.structure,
      implementation_difficulty: selectedIdea.implementation_difficulty,
      retention_prediction: selectedIdea.retention_prediction,
      exit_strategy_impact: selectedIdea.exit_strategy_impact,
      creative_hook: selectedIdea.creative_hook
    };

    const pricingResult = await businessService.saveField('pricing_data', JSON.stringify(updatedPricingData));
    const modelResult = await businessService.saveField('recurring_model_details', JSON.stringify(recurringModelDetails));

    setSaving(false);

    if (pricingResult.success && modelResult.success) {
      navigate('/customer-experience');
    } else {
      setError('Failed to save recurring model');
    }
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading...</p>
      </Container>
    );
  }

  // INTRO STAGE
  if (stage === 'intro') {
    return (
      <Container className="py-5">
        <BackToDashboard module="business-model" />
        <Row className="justify-content-center">
          <Col md={10} lg={8}>
            <Card className="shadow">
              <Card.Body className="p-4">
                <h2 className="mb-4">💡 Recurring Revenue Workshop</h2>

                <Alert variant="info">
                  <strong>
                    {context.core_product
                      ? `Let's explore turning "${context.core_product}" into a recurring revenue opportunity!`
                      : "Let's explore recurring revenue opportunities together!"}
                  </strong>
                  <p className="mb-0 mt-2">
                    Your product has recurring revenue potential - we just need to find it.
                    This workshop will help you discover creative ways to build predictable income from what you already offer.
                  </p>
                </Alert>

                <Card className="mb-4 bg-light">
                  <Card.Body>
                    <h6>Why Recurring Revenue Matters:</h6>
                    <Row className="mt-3">
                      <Col md={4} className="text-center mb-3">
                        <div className="p-3 bg-white rounded">
                          <h5 className="text-primary">📈 Predictable</h5>
                          <small>Know your revenue before the month starts</small>
                        </div>
                      </Col>
                      <Col md={4} className="text-center mb-3">
                        <div className="p-3 bg-white rounded">
                          <h5 className="text-success">💰 Higher Value</h5>
                          <small>3-8x valuation vs one-time models</small>
                        </div>
                      </Col>
                      <Col md={4} className="text-center mb-3">
                        <div className="p-3 bg-white rounded">
                          <h5 className="text-warning">🔄 Less Hustle</h5>
                          <small>Stop finding new customers every month</small>
                        </div>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>

                <p className="text-muted">
                  <strong>How this works:</strong> We'll have a conversation about your business.
                  I'll ask questions, share creative ideas from other industries, and together
                  we'll discover recurring revenue opportunities that fit YOUR business.
                </p>

                {error && <Alert variant="danger">{error}</Alert>}

                <div className="d-flex justify-content-between mt-4">
                  <Button variant="outline-secondary" onClick={handleSkip}>
                    Skip Workshop
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleStartWorkshop}
                    disabled={generating}
                  >
                    {generating ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Starting...
                      </>
                    ) : (
                      "Let's Explore! →"
                    )}
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  // CONVERSATION STAGE
  if (stage === 'conversation') {
    const aiMessages = conversation.filter(msg => msg.role === 'assistant');
    const lastAiMessage = aiMessages[aiMessages.length - 1];

    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={10} lg={8}>
            <Card className="shadow">
              <Card.Body className="p-4">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h2>💬 Recurring Revenue Discovery</h2>
                  <small className="text-muted">
                    Exchange {Math.ceil(conversation.length / 2)} of ~3-5
                  </small>
                </div>

                {error && <Alert variant="danger">{error}</Alert>}

                {/* AI Message */}
                <div className="mb-4 p-3 bg-light rounded" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  <strong className="text-primary">🤖 AI Coach:</strong>
                  {generating && conversation.length > 0 && conversation[conversation.length - 1].role === 'user' ? (
                    <div className="text-center py-3">
                      <Spinner animation="border" size="sm" variant="primary" />
                      <span className="ms-2 text-muted">Thinking...</span>
                    </div>
                  ) : (
                    <div className="mt-2" style={{ whiteSpace: 'pre-wrap' }}>
                      {lastAiMessage?.content}
                    </div>
                  )}
                </div>

                {/* User Input */}
                <Form.Group className="mb-3">
                  <Form.Label><strong>You:</strong></Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    value={userInput}
                    onChange={(e) => setUserInput(e.target.value)}
                    placeholder="Share your thoughts, ask questions, or describe your situation..."
                    disabled={generating}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                  />
                  <Form.Text className="text-muted">
                    Press Enter to send, Shift+Enter for new line
                  </Form.Text>
                </Form.Group>

                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                  <Button
                    variant="outline-secondary"
                    onClick={() => navigate('/dashboard')}
                    disabled={generating}
                  >
                    Save & Continue Later
                  </Button>

                  <div className="d-flex gap-2">
                    {(readyForIdeas || conversation.length >= 6) && (
                      <Button
                        variant="success"
                        onClick={handleGenerateIdeas}
                        disabled={generating}
                      >
                        I'm Ready - Show Me Ideas!
                      </Button>
                    )}

                    <Button
                      variant="primary"
                      onClick={handleSendMessage}
                      disabled={generating || !userInput.trim()}
                    >
                      {generating ? 'Sending...' : 'Send'}
                    </Button>
                  </div>
                </div>

                {conversation.length >= 4 && !readyForIdeas && (
                  <div className="text-center mt-3">
                    <small className="text-muted">
                      Feel like we've covered enough? Click "I'm Ready" when you want to see personalized ideas.
                    </small>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  // GENERATING STAGE
  if (stage === 'generating') {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={10} lg={8}>
            <Card className="shadow">
              <Card.Body className="p-5 text-center">
                <Spinner animation="border" variant="primary" className="mb-3" />
                <h4>Creating Your Personalized Ideas...</h4>
                <p className="text-muted">
                  Based on our conversation, I'm crafting recurring revenue models
                  specifically for your business. This takes about 30-60 seconds.
                </p>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  // IDEAS STAGE
  if (stage === 'ideas') {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={10} lg={9}>
            <Card className="shadow">
              <Card.Body className="p-4">
                <h2 className="mb-4">✨ Your Personalized Recurring Revenue Ideas</h2>

                <Alert variant="success">
                  Based on our conversation, here are 5 tailored ideas for YOUR business:
                </Alert>

                {error && <Alert variant="danger">{error}</Alert>}

                {recurringIdeas.map((idea, index) => (
                  <Card key={index} className="mb-3 border-primary">
                    <Card.Body>
                      <h5>{index + 1}. {idea.model_name}</h5>

                      {idea.creative_hook && (
                        <Alert variant="light" className="py-2">
                          <strong>💡 The Hook:</strong> {idea.creative_hook}
                        </Alert>
                      )}

                      <Row className="mt-3">
                        <Col md={6}>
                          <h6>Structure:</h6>
                          <p className="small">{idea.structure}</p>
                        </Col>
                        <Col md={6}>
                          <h6>Pricing:</h6>
                          <p className="small">
                            One-time: ${idea.one_time_price}<br />
                            Monthly: ${idea.monthly_price}<br />
                            <strong>First-year revenue: ${idea.first_year_revenue}</strong>
                          </p>
                        </Col>
                      </Row>

                      <Row className="mt-2">
                        <Col md={12}>
                          <h6>Why Your Customers Would Love This:</h6>
                          <p className="small">{idea.customer_value}</p>
                        </Col>
                      </Row>

                      <Row className="mt-2">
                        <Col md={4}>
                          <small><strong>Implementation:</strong> {idea.implementation_difficulty}</small>
                        </Col>
                        <Col md={4}>
                          <small><strong>Retention:</strong> {idea.retention_prediction}</small>
                        </Col>
                        <Col md={4}>
                          <small><strong>Exit Impact:</strong> {idea.exit_strategy_impact}</small>
                        </Col>
                      </Row>

                      <Button
                        variant="primary"
                        className="mt-3"
                        onClick={() => handleSelectIdea(index)}
                      >
                        Select This Model
                      </Button>
                    </Card.Body>
                  </Card>
                ))}

                <div className="d-flex justify-content-between mt-4">
                  <Button
                    variant="outline-secondary"
                    onClick={() => setStage('conversation')}
                  >
                    ← Back to Conversation
                  </Button>
                  <Button
                    variant="outline-secondary"
                    onClick={handleSkip}
                  >
                    Skip - Keep One-Time Model
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  // REFINE STAGE
  if (stage === 'refine') {
    const selectedIdea = recurringIdeas[selectedIdeaIndex];
    const originalValuation = parseFloat(context.current_pricing || 100) * 12 * 2;
    const newValuation = (parseFloat(refinedOneTime || 0) + (parseFloat(refinedMonthly || 0) * 12)) * 5;
    const increase = originalValuation > 0 ? ((newValuation - originalValuation) / originalValuation * 100).toFixed(0) : 0;

    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={10} lg={8}>
            <Card className="shadow">
              <Card.Body className="p-4">
                <h2 className="mb-4">✏️ Customize Your Model</h2>

                <Alert variant="info">
                  <strong>Selected:</strong> {selectedIdea.model_name}
                  {selectedIdea.creative_hook && (
                    <p className="mb-0 mt-1 small"><em>{selectedIdea.creative_hook}</em></p>
                  )}
                </Alert>

                {error && <Alert variant="danger">{error}</Alert>}

                <Form.Group className="mb-3">
                  <Form.Label>One-time component ($)</Form.Label>
                  <Form.Control
                    type="number"
                    value={refinedOneTime}
                    onChange={(e) => setRefinedOneTime(e.target.value)}
                    placeholder="497"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Monthly recurring ($)</Form.Label>
                  <Form.Control
                    type="number"
                    value={refinedMonthly}
                    onChange={(e) => setRefinedMonthly(e.target.value)}
                    placeholder="49"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>What's included in the recurring:</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    value={includedFeatures.join('\n')}
                    onChange={(e) => setIncludedFeatures(e.target.value.split('\n'))}
                    placeholder="One feature per line"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Billing options:</Form.Label>
                  <Form.Check
                    type="radio"
                    id="billing-monthly"
                    name="billing"
                    label="Monthly only"
                    checked={billingOption === 'monthly'}
                    onChange={() => setBillingOption('monthly')}
                  />
                  <Form.Check
                    type="radio"
                    id="billing-all"
                    name="billing"
                    label={`All options (Monthly, Quarterly -${quarterlyDiscount}%, Annual -${annualDiscount}%)`}
                    checked={billingOption === 'all'}
                    onChange={() => setBillingOption('all')}
                  />
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label>Trial period:</Form.Label>
                  <Form.Select
                    value={trialPeriod}
                    onChange={(e) => setTrialPeriod(e.target.value)}
                  >
                    <option value="none">No trial</option>
                    <option value="7-day">7-day free trial</option>
                    <option value="14-day">14-day free trial</option>
                    <option value="30-day">30-day free trial</option>
                  </Form.Select>
                </Form.Group>

                <Card className="mb-4 bg-light border-success">
                  <Card.Body>
                    <h5>💰 Potential Valuation Impact</h5>
                    <Row className="text-center">
                      <Col>
                        <small>Before (One-time @2x)</small>
                        <h4 className="text-muted">${originalValuation.toLocaleString()}</h4>
                      </Col>
                      <Col>
                        <small>After (Recurring @5x)</small>
                        <h4 className="text-success">${newValuation.toLocaleString()}</h4>
                      </Col>
                      <Col>
                        <small>Increase</small>
                        <h4 className="text-primary">+{increase}%</h4>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>

                <div className="d-flex justify-content-between">
                  <Button
                    variant="outline-secondary"
                    onClick={() => setStage('ideas')}
                  >
                    ← Try Different Idea
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleSaveModel}
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save & Continue →'}
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  return null;
}

export default RecurringRevenueWorkshop;
